import { useEffect, useState, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import {
    XMarkIcon,
    ArrowPathIcon,
    CheckCircleIcon,
    ExclamationTriangleIcon,
    BoltIcon,
} from '@heroicons/react/24/outline';

const API = () => `${process.env.REACT_APP_IP}:${process.env.REACT_APP_PORT}`;

// Descriptif métier par cible (aligné sur la matrice codec du parc).
const TARGET_INFO = {
    hevc4k: { title: '4K HEVC', sub: 'VIM3/VIM3L · players webOS/Tizen 4K' },
    h264_4k: { title: '4K H.264', sub: 'Electron / Windows (HEVC 4K = TDR)' },
    fhd: { title: '1080p H.264', sub: 'Tous les écrans FHD (LG/Samsung/VIM1)' },
    reencode: { title: 'Ré-encoder en H.264', sub: 'Répare une vidéo FHD qui lit mal (même résolution)' },
};

function fmtEta(sec) {
    if (sec == null) return '—';
    if (sec < 60) return `${sec}s`;
    const m = Math.floor(sec / 60), s = sec % 60;
    return `${m}min ${String(s).padStart(2, '0')}s`;
}
function fmtDur(sec) {
    if (!sec) return '—';
    const m = Math.floor(sec / 60), s = Math.round(sec % 60);
    return `${m}:${String(s).padStart(2, '0')}`;
}

export default function ModalConvertVideo({ isVisible, onClose, fileName, dirName, onDone }) {
    const [loading, setLoading] = useState(true);
    const [source, setSource] = useState(null);
    const [targets, setTargets] = useState([]);
    const [probeError, setProbeError] = useState(null);
    const [job, setJob] = useState(null);          // { state, progressPct, etaSec, speed, outName, error }
    const [busyKey, setBusyKey] = useState(null);   // cible en cours
    const pollRef = useRef(null);

    const stopPoll = () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };

    // Probe à l'ouverture.
    useEffect(() => {
        if (!isVisible || !fileName) return;
        setLoading(true); setSource(null); setTargets([]); setProbeError(null);
        setJob(null); setBusyKey(null); stopPoll();
        axios.post(`${API()}/media/probe`, { fileName, dirName })
            .then((res) => { setSource(res.data.source); setTargets(res.data.targets || []); })
            .catch((err) => setProbeError(err.response?.data?.error || 'Analyse impossible.'))
            .finally(() => setLoading(false));
        return stopPoll;
    }, [isVisible, fileName, dirName]);

    const poll = useCallback((jobId) => {
        stopPoll();
        pollRef.current = setInterval(() => {
            axios.get(`${API()}/media/convert/${jobId}`)
                .then((res) => {
                    setJob(res.data);
                    if (res.data.state === 'done') {
                        stopPoll(); setBusyKey(null);
                        toast.success(`Variante générée : ${res.data.outName}`);
                        onDone && onDone();
                    } else if (res.data.state === 'error') {
                        stopPoll(); setBusyKey(null);
                        toast.error(`Échec : ${res.data.error || 'erreur ffmpeg'}`);
                    }
                })
                .catch(() => { /* transitoire : on réessaie au prochain tick */ });
        }, 1000);
    }, [onDone]);

    const startConvert = (targetKey) => {
        setBusyKey(targetKey);
        setJob({ state: 'queued', progressPct: 0, etaSec: null, speed: null });
        axios.post(`${API()}/media/convert`, { fileName, dirName, target: targetKey })
            .then((res) => poll(res.data.jobId))
            .catch((err) => {
                setBusyKey(null); setJob(null);
                toast.error(err.response?.data?.error || 'Conversion refusée.');
            });
    };

    if (!isVisible) return null;

    const converting = job && (job.state === 'queued' || job.state === 'running');
    const done = job && job.state === 'done';

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4" onClick={converting ? undefined : onClose}>
            <div className="w-[92vw] max-w-lg rounded-2xl bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
                {/* En-tête */}
                <div className="flex items-start justify-between border-b border-slate-200 px-5 py-4">
                    <div className="min-w-0">
                        <h3 className="flex items-center gap-2 text-base font-semibold text-slate-800">
                            <BoltIcon className="h-5 w-5 text-indigo-500" /> Convertir la vidéo
                        </h3>
                        <p className="mt-0.5 truncate text-sm text-slate-500" title={fileName}>{fileName}</p>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={converting}
                        className="rounded-lg p-1.5 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 disabled:cursor-not-allowed disabled:opacity-40"
                        title={converting ? 'Conversion en cours…' : 'Fermer'}
                    >
                        <XMarkIcon className="h-5 w-5" />
                    </button>
                </div>

                <div className="px-5 py-4">
                    {/* Source */}
                    {loading ? (
                        <p className="py-8 text-center text-sm text-slate-400">Analyse du fichier…</p>
                    ) : probeError ? (
                        <div className="flex items-center gap-2 rounded-xl bg-red-50 px-3 py-2.5 text-sm text-red-700">
                            <ExclamationTriangleIcon className="h-5 w-5 shrink-0" /> {probeError}
                        </div>
                    ) : (
                        <>
                            <div className="mb-4 grid grid-cols-3 gap-2 rounded-xl bg-slate-50 p-3 text-center">
                                <div>
                                    <p className="text-[11px] uppercase tracking-wide text-slate-400">Codec</p>
                                    <p className="text-sm font-semibold text-slate-700">{(source.vcodec || '—').toUpperCase()}</p>
                                </div>
                                <div>
                                    <p className="text-[11px] uppercase tracking-wide text-slate-400">Résolution</p>
                                    <p className="text-sm font-semibold text-slate-700">{source.width}×{source.height}</p>
                                </div>
                                <div>
                                    <p className="text-[11px] uppercase tracking-wide text-slate-400">Durée</p>
                                    <p className="text-sm font-semibold text-slate-700">{fmtDur(source.durationSec)}</p>
                                </div>
                            </div>

                            {targets.length === 0 ? (
                                <div className="flex items-center gap-2 rounded-xl bg-amber-50 px-3 py-2.5 text-sm text-amber-700">
                                    <ExclamationTriangleIcon className="h-5 w-5 shrink-0" />
                                    Aucune conversion disponible pour cette source (résolution trop faible ou déjà optimale).
                                </div>
                            ) : (
                                <>
                                    <p className="mb-2 text-xs font-medium uppercase tracking-wide text-slate-400">Générer la variante</p>
                                    <div className="space-y-2">
                                        {targets.map((t) => {
                                            const info = TARGET_INFO[t.key] || { title: t.label, sub: '' };
                                            const isBusy = busyKey === t.key;
                                            const disabled = t.exists || converting;
                                            return (
                                                <button
                                                    key={t.key}
                                                    type="button"
                                                    disabled={disabled}
                                                    onClick={() => startConvert(t.key)}
                                                    className={`flex w-full items-center justify-between rounded-xl border px-4 py-3 text-left transition ${
                                                        t.exists
                                                            ? 'cursor-default border-emerald-200 bg-emerald-50'
                                                            : converting
                                                                ? 'cursor-not-allowed border-slate-200 bg-slate-50 opacity-60'
                                                                : 'border-slate-200 hover:border-indigo-400 hover:bg-indigo-50'
                                                    }`}
                                                >
                                                    <span>
                                                        <span className="block text-sm font-semibold text-slate-800">{info.title}</span>
                                                        <span className="block text-xs text-slate-500">{info.sub}</span>
                                                    </span>
                                                    {t.exists ? (
                                                        <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600">
                                                            <CheckCircleIcon className="h-4 w-4" /> déjà générée
                                                        </span>
                                                    ) : isBusy ? (
                                                        <ArrowPathIcon className="h-5 w-5 animate-spin text-indigo-500" />
                                                    ) : (
                                                        <span className="text-xs font-semibold text-indigo-600">Convertir →</span>
                                                    )}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </>
                            )}

                            {/* Progression */}
                            {job && (
                                <div className={`mt-4 rounded-xl border p-3 ${done ? 'border-emerald-200 bg-emerald-50' : 'border-slate-200 bg-slate-50'}`}>
                                    <div className="mb-1.5 flex items-center justify-between text-xs">
                                        <span className="font-medium text-slate-600">
                                            {job.state === 'queued' ? 'En file…' : job.state === 'running' ? 'Conversion en cours' : done ? 'Terminé' : 'Erreur'}
                                        </span>
                                        <span className="font-mono tabular-nums text-slate-500">
                                            {done ? '100%' : `${job.progressPct || 0}%`}
                                            {job.state === 'running' && job.speed ? ` · ${job.speed}× · ETA ${fmtEta(job.etaSec)}` : ''}
                                        </span>
                                    </div>
                                    <div className="h-2 w-full overflow-hidden rounded-full bg-slate-200">
                                        <div
                                            className={`h-full rounded-full transition-all duration-500 ${done ? 'bg-emerald-500' : 'bg-indigo-500'}`}
                                            style={{ width: `${done ? 100 : (job.progressPct || 0)}%` }}
                                        />
                                    </div>
                                    {done && (
                                        <div className="mt-2 text-xs text-emerald-700">
                                            <div className="flex items-center gap-1.5">
                                                <span className="min-w-0 break-all font-medium">✓ {job.outName}</span>
                                                <button
                                                    type="button"
                                                    title="Copier le nom du fichier"
                                                    onClick={() => {
                                                        try {
                                                            navigator.clipboard.writeText(job.outName);
                                                            toast.info(`Nom copié : ${job.outName}`, { autoClose: 2000 });
                                                        } catch (e) { /* presse-papier indisponible */ }
                                                    }}
                                                    className="shrink-0 rounded p-1 text-emerald-600 transition hover:bg-emerald-100 hover:text-emerald-800"
                                                >
                                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-3.5 w-3.5"><rect x="9" y="9" width="11" height="11" rx="2" /><path d="M5 15V5a2 2 0 0 1 2-2h10" /></svg>
                                                </button>
                                            </div>
                                            <span className="opacity-80">MD5 enregistré, le parc la récupérera.</span>
                                        </div>
                                    )}
                                </div>
                            )}
                        </>
                    )}
                </div>

                <div className="flex justify-end gap-2 border-t border-slate-200 px-5 py-3">
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={converting}
                        className="rounded-lg px-4 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-40"
                    >
                        {done ? 'Fermer' : converting ? 'Conversion…' : 'Fermer'}
                    </button>
                </div>
            </div>
        </div>
    );
}
